import * as React from 'react';
import { Percent, Trash2, X } from 'lucide-react';
import { RowError, rowErrorRingClass, type RowErrorReason } from '../ui/RowError';
import { dedupeMessages, fieldHasError } from '../../lib/line-item-validation';
import {
  DISCOUNT_ESTIMATE_NOTE,
  DISCOUNT_KIND_LABEL,
  ISSUE_HELPER,
  ISSUE_PERCENT_NOTE,
  MULTI_RIG_ALREADY_ON_TICKET,
  MULTI_RIG_DEFAULT_PERCENT,
  MULTI_RIG_HELPER,
  discountDescription,
  fmtLineMoney,
  hasMultiRig,
  isDiscountEstimate,
  type DiscountEdit,
  type DiscountKind,
  type DiscountSpec,
  type DiscountableLine,
  type IssueMode,
} from '../../lib/ticket-discount';

/**
 * "Add discount" and the discount line's own row, shared by the office editor
 * (NewTicketScreen) and the field editor (FieldTicketsScreen). What a discount
 * is lives in lib/ticket-discount.ts; this file is only how it is entered.
 *
 * The pay wording ("Reduces hands' pay.") is rendered here and nowhere else. The
 * customer documents are drawn by the server, and the in-app preview reads the
 * description — which never carries it.
 */

const inputClass = (bad: boolean) =>
  `w-full bg-fieldiq-card rounded px-2 py-1 text-xs border outline-none ${bad ? 'border-red-500/60' : 'border-fieldiq-border'}`;

/** '' stays blank (null); anything typed, 0 included, is a number. */
function readNumber(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

const blurOnWheel = (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur();

function ModeToggle({ mode, onChange }: { mode: IssueMode; onChange: (m: IssueMode) => void }) {
  return (
    <div role="radiogroup" aria-label="Discount in" className="inline-flex justify-self-start self-end rounded border border-fieldiq-border overflow-hidden text-xs">
      {(['amount', 'percent'] as const).map(m => (
        <button
          key={m}
          type="button"
          role="radio"
          aria-checked={mode === m}
          onClick={() => onChange(m)}
          className={`px-2 py-1 ${mode === m ? 'bg-fi-amber text-fi-signal-ink font-semibold' : 'bg-fieldiq-card text-fieldiq-text'}`}
        >
          {m === 'amount' ? '$' : '%'}
        </button>
      ))}
    </div>
  );
}

export interface AddDiscountButtonProps {
  lines: readonly DiscountableLine[];
  onAdd: (spec: DiscountSpec) => void;
}

/**
 * The button and its small form. The first choice is the type; Multi Rig is
 * offered once per ticket and says why when it is not.
 */
export function AddDiscountButton({ lines, onAdd }: AddDiscountButtonProps) {
  const [open, setOpen] = React.useState(false);
  const [kind, setKind] = React.useState<DiscountKind | null>(null);
  const [percent, setPercent] = React.useState<string>(String(MULTI_RIG_DEFAULT_PERCENT));
  const [name, setName] = React.useState('');
  const [mode, setMode] = React.useState<IssueMode>('amount');
  const [value, setValue] = React.useState('');
  const multiRigTaken = hasMultiRig(lines);

  const reset = () => {
    setOpen(false);
    setKind(null);
    setPercent(String(MULTI_RIG_DEFAULT_PERCENT));
    setName('');
    setMode('amount');
    setValue('');
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="text-xs flex items-center gap-1 text-fieldiq-accent">
        <Percent size={14} /> Add discount
      </button>
    );
  }

  const canAdd =
    kind === 'multi_rig'
      ? !multiRigTaken && readNumber(percent) != null
      : kind === 'issue'
        ? name.trim() !== '' && readNumber(value) != null
        : false;

  const add = () => {
    if (!canAdd || !kind) return;
    if (kind === 'multi_rig') onAdd({ kind, percent: readNumber(percent) });
    else onAdd({ kind, name: name.trim(), mode, percent: mode === 'percent' ? readNumber(value) : null, amount: mode === 'amount' ? readNumber(value) : null });
    reset();
  };

  return (
    <div
      role="group"
      aria-label="Add discount"
      data-testid="add-discount-panel"
      className="w-full mt-2 bg-fieldiq-dark rounded-xl p-3 space-y-2 border border-fieldiq-border"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-fieldiq-text">Add discount</span>
        <button type="button" aria-label="Cancel discount" onClick={reset} className="p-1 text-fieldiq-muted">
          <X size={14} />
        </button>
      </div>
      <div role="radiogroup" aria-label="Discount type" className="flex gap-2">
        {(['multi_rig', 'issue'] as const).map(k => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={kind === k}
            disabled={k === 'multi_rig' && multiRigTaken}
            onClick={() => setKind(k)}
            className={`px-3 py-1.5 rounded-full text-xs border ${kind === k ? 'bg-fi-amber text-fi-signal-ink border-black/20 font-semibold' : 'bg-fieldiq-card text-fieldiq-text border-fieldiq-border'} disabled:opacity-50`}
          >
            {DISCOUNT_KIND_LABEL[k]}
          </button>
        ))}
      </div>
      {multiRigTaken && <p className="text-[11px] text-fieldiq-muted">{MULTI_RIG_ALREADY_ON_TICKET}</p>}

      {kind === 'multi_rig' && (
        <div className="space-y-1">
          <label className="text-[10px] text-fieldiq-muted" htmlFor="add-discount-multi-rig-percent">Percent</label>
          <input
            id="add-discount-multi-rig-percent"
            type="number"
            step="0.01"
            inputMode="decimal"
            onWheel={blurOnWheel}
            value={percent}
            onChange={e => setPercent(e.target.value)}
            className={inputClass(false)}
          />
          <p className="text-[11px] text-fieldiq-muted">
            {discountDescription({ kind, percent: readNumber(percent) })} · {MULTI_RIG_HELPER}
          </p>
        </div>
      )}

      {kind === 'issue' && (
        <div className="space-y-2">
          <div>
            <label className="text-[10px] text-fieldiq-muted" htmlFor="add-discount-issue-name">Name</label>
            <input
              id="add-discount-issue-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="What went wrong"
              className={inputClass(false)}
            />
          </div>
          <div className="flex items-end gap-2">
            <ModeToggle mode={mode} onChange={setMode} />
            <div className="flex-1">
              <label className="text-[10px] text-fieldiq-muted" htmlFor="add-discount-issue-value">
                {mode === 'amount' ? 'Amount ($)' : 'Percent'}
              </label>
              <input
                id="add-discount-issue-value"
                type="number"
                step="0.01"
                inputMode="decimal"
                onWheel={blurOnWheel}
                value={value}
                onChange={e => setValue(e.target.value)}
                className={inputClass(false)}
              />
            </div>
          </div>
          <p className="text-[11px] text-fieldiq-muted">
            {ISSUE_HELPER}
            {mode === 'percent' ? ` ${ISSUE_PERCENT_NOTE}` : ''}
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={add}
          disabled={!canAdd}
          className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
        >
          Add
        </button>
      </div>
    </div>
  );
}

export interface DiscountLineRowProps {
  line: DiscountableLine;
  index: number;
  /** Every line on the ticket, as shown — decides whether this amount is an estimate. */
  lines: readonly DiscountableLine[];
  currency?: string | null;
  /** This row's refusals, local or from the API, keyed exactly as the plain rows' are. */
  reasons?: RowErrorReason[];
  onEdit: (edit: DiscountEdit) => void;
  onRemove: () => void;
  rowRef?: (el: HTMLDivElement | null) => void;
  /** Each editor's row fill: the office rows sit on the card, the field rows on the page. */
  surface?: 'dark' | 'card';
}

/**
 * The discount line in the editor list. Editable and removable like any line;
 * no type, unit, rate, quantity or crew boxes — the server prices the line and
 * nobody did the work.
 */
export function DiscountLineRow({ line, index, lines, currency, reasons, onEdit, onRemove, rowRef, surface = 'dark' }: DiscountLineRowProps) {
  const kind = line.discountKind as DiscountKind;
  const mode: IssueMode = line.discountIssueMode ?? 'amount';
  const estimate = isDiscountEstimate(line, lines);
  const all = dedupeMessages(reasons ?? []);
  const pid = `discount-${index}`;

  return (
    <div
      ref={rowRef}
      data-testid="discount-line-row"
      className={`${surface === 'card' ? 'bg-fieldiq-card' : 'bg-fieldiq-dark'} rounded-xl p-3 space-y-2 ${rowErrorRingClass(all.length > 0)}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-fieldiq-muted">Discount · {DISCOUNT_KIND_LABEL[kind]}</p>
          <p className="text-sm font-medium text-fieldiq-text break-words" data-testid="discount-line-label">{line.description}</p>
        </div>
        <div className="flex items-start gap-2 flex-shrink-0">
          <div className="text-right">
            <span data-testid="discount-line-amount" className="text-sm font-semibold text-fieldiq-text">
              {fmtLineMoney(line.total, currency)}
            </span>
            {estimate && <p className="text-[10px] text-fieldiq-muted">{DISCOUNT_ESTIMATE_NOTE}</p>}
          </div>
          <button type="button" aria-label={`Remove line ${index + 1}`} onClick={onRemove} className="p-1 text-red-400 hover:text-red-300">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {kind === 'multi_rig' ? (
        <div className="max-w-[160px]">
          <label className="text-[10px] text-fieldiq-muted" htmlFor={`${pid}-percent`}>Percent</label>
          <input
            id={`${pid}-percent`}
            type="number"
            step="0.01"
            inputMode="decimal"
            onWheel={blurOnWheel}
            value={line.discountPercent ?? ''}
            onChange={e => onEdit({ field: 'percent', value: readNumber(e.target.value) })}
            className={inputClass(fieldHasError(reasons, 'discount_percent'))}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_160px] gap-2 items-end">
          <div>
            <label className="text-[10px] text-fieldiq-muted" htmlFor={`${pid}-name`}>Name</label>
            <input
              id={`${pid}-name`}
              type="text"
              value={line.discountName ?? ''}
              onChange={e => onEdit({ field: 'name', value: e.target.value })}
              className={inputClass(fieldHasError(reasons, 'description'))}
            />
          </div>
          <ModeToggle mode={mode} onChange={m => onEdit({ field: 'mode', value: m })} />
          <div>
            <label className="text-[10px] text-fieldiq-muted" htmlFor={`${pid}-value`}>
              {mode === 'amount' ? 'Amount ($)' : 'Percent'}
            </label>
            <input
              id={`${pid}-value`}
              type="number"
              step="0.01"
              inputMode="decimal"
              onWheel={blurOnWheel}
              value={(mode === 'amount' ? line.discountAmount : line.discountPercent) ?? ''}
              onChange={e => onEdit({ field: mode, value: readNumber(e.target.value) } as DiscountEdit)}
              className={inputClass(fieldHasError(reasons, mode === 'amount' ? 'discount_amount' : 'discount_percent'))}
            />
          </div>
        </div>
      )}

      <p className="text-[11px] text-fieldiq-muted">
        {kind === 'multi_rig' ? MULTI_RIG_HELPER : ISSUE_HELPER}
        {kind === 'issue' && mode === 'percent' ? ` ${ISSUE_PERCENT_NOTE}` : ''}
      </p>
      {all.length > 0 && <RowError reasons={all} />}
    </div>
  );
}
